import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { useUnit } from '../contexts/UnitContext';
import { suggestionApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { formatDate, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Lightbulb, Search, X, History, Check } from 'lucide-react';
import ParticipationSummary from '../components/ParticipationSummary';

// Last 24 months, newest first. Merged with whatever the server reports as
// having data, so a month with submissions is always selectable even if it
// falls outside this window.
function recentPeriods() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 24; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      period: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    });
  }
  return out;
}

const currentPeriod = () => recentPeriods()[0].period;

function StatusPill({ status }) {
  const submitted = status === 'Submitted';
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
      submitted ? 'bg-[#E7F7EF] text-[#15654B]' : 'bg-[#FBF0E6] text-[#B14A0E]'}`}>
      {status}
    </span>
  );
}

function HistoryDrawer({ employeeId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    suggestionApi.history(employeeId)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || 'Could not load history'); });
    return () => { alive = false; };
  }, [employeeId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-[520px] h-full bg-white shadow-xl flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[#E6E9F0]">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#5A6377]">
              <History className="w-3.5 h-3.5" /> Suggestion history
            </div>
            <h2 className="text-lg font-semibold text-[#11182B] truncate mt-1">{data?.name || '…'}</h2>
            {data && (
              <p className="text-xs text-[#5A6377] truncate">
                {data.card_no || '—'} · {data.department}{data.unit ? ` · ${data.unit}` : ''} · {data.total} total
              </p>
            )}
          </div>
          <button onClick={onClose} aria-label="Close history"
            className="text-[#98A1B2] hover:text-[#11182B] p-1 rounded hover:bg-[#F2F4F8]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {err && <p className="text-sm text-[#C2151F]">{err}</p>}
          {!data && !err && <p className="text-sm text-[#5A6377]">Loading…</p>}
          {data && data.months.length === 0 && (
            <p className="text-sm text-[#98A1B2] py-10 text-center">
              This employee has not submitted any suggestions yet.
            </p>
          )}
          <div className="space-y-5">
            {data?.months.map((m) => (
              <div key={m.period}>
                <div className="flex items-baseline gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-[#11182B]">{m.month_label}</h3>
                  <span className="text-[11px] text-[#98A1B2]">{m.count} submission{m.count === 1 ? '' : 's'}</span>
                </div>
                <div className="space-y-2">
                  {m.items.map((s) => (
                    <div key={s.id} className="border border-[#E6E9F0] rounded-lg p-3">
                      <p className="text-sm text-[#11182B] whitespace-pre-wrap">{s.text}</p>
                      <div className="flex items-center justify-between gap-2 mt-2 text-[11px] text-[#98A1B2]">
                        <span>Submitted {formatDateTime(s.created_at)} · {s.source === 'self' ? 'Self (QR)' : 'HR'}</span>
                        <span className={s.status === 'reviewed' ? 'text-[#15654B]' : 'text-[#8A5A12]'}>
                          {s.status === 'reviewed' ? 'Reviewed' : 'New'}
                        </span>
                      </div>
                      {s.remark ? <p className="text-[11px] text-[#5A6377] mt-1">HR note: {s.remark}</p> : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Suggestions() {
  const { hasPerm } = useAuth();
  const { activeUnit } = useUnit();
  const canReview = hasPerm('employee.edit');

  const [period, setPeriod] = useState(currentPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dept, setDept] = useState('');
  const [search, setSearch] = useState('');
  const [historyFor, setHistoryFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await suggestionApi.monthly({ period, unit: activeUnit });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load suggestions');
      setData(null);
    } finally { setLoading(false); }
  }, [period, activeUnit]);

  useEffect(() => { load(); }, [load]);

  // Server-reported months (those with data) merged with a rolling window.
  const periodOptions = useMemo(() => {
    const seen = new Map();
    (data?.periods || []).forEach((p) => seen.set(p.period, p.label));
    recentPeriods().forEach((p) => { if (!seen.has(p.period)) seen.set(p.period, p.label); });
    return [...seen.entries()]
      .map(([p, label]) => ({ period: p, label }))
      .sort((a, b) => b.period.localeCompare(a.period));
  }, [data]);

  const depts = useMemo(
    () => [...new Set((data?.rows || []).map((r) => r.department).filter(Boolean))].sort(),
    [data],
  );

  const rows = useMemo(() => {
    let list = data?.rows || [];
    if (statusFilter) list = list.filter((r) => r.status === statusFilter);
    if (dept) list = list.filter((r) => r.department === dept);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        (r.name || '').toLowerCase().includes(q)
        || (r.card_no || '').toLowerCase().includes(q)
        || (r.suggestions || []).some((s) => (s.text || '').toLowerCase().includes(q)));
    }
    return list;
  }, [data, statusFilter, dept, search]);

  const review = async (s) => {
    try {
      await suggestionApi.review(s.id, { status: 'reviewed' });
      toast.success('Marked reviewed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };

  const summary = data?.summary || { total: 0, submitted: 0, pending: 0 };
  const pct = summary.total ? Math.round((summary.submitted / summary.total) * 100) : 0;

  const stat = (label, value, tone) => (
    <div className="bg-white border border-[#E6E9F0] rounded-xl px-4 py-3 flex-1 min-w-[130px]">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2]">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-0.5 ${tone}`}>{value}</div>
    </div>
  );

  return (
    <div data-testid="suggestions-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1200px] mx-auto">

            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                     style={{ background: 'linear-gradient(135deg,#15654B,#15A36E)' }}>
                  <Lightbulb className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">Suggestions</h1>
                  <p className="text-sm text-[#5A6377]">
                    Month-wise register{activeUnit ? ` · ${activeUnit}` : ''} — who has submitted, who is still pending.
                  </p>
                </div>
              </div>
              <select data-testid="suggestions-period" value={period} onChange={(e) => setPeriod(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-3 py-2 min-w-[180px]">
                {periodOptions.map((p) => <option key={p.period} value={p.period}>{p.label}</option>)}
              </select>
            </div>

            <ParticipationSummary fetcher={suggestionApi.summary} unit={activeUnit}
              noun="suggestion" nounPlural="Suggestions" accent="#15A36E" />

            <h2 className="text-sm font-semibold text-[#11182B] mb-3">
              {data?.month_label || ''} register
              {data?.window && (
                <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full align-middle ${
                  data.window.open ? 'bg-[#E7F7EF] text-[#15654B]' : 'bg-[#F2F4F8] text-[#5A6377]'}`}>
                  {data.window.open ? `Open till ${data.window.deadline_label}` : `Closed ${data.window.deadline_label}`}
                </span>
              )}
            </h2>

            <div className="flex gap-3 mb-5 flex-wrap">
              {stat('Employees', summary.total, 'text-[#11182B]')}
              {stat('Submitted', summary.submitted, 'text-[#15654B]')}
              {stat('Pending', summary.pending, 'text-[#B14A0E]')}
              {stat('Coverage', `${pct}%`, 'text-[#11182B]')}
            </div>

            <div className="flex gap-2 mb-4 flex-wrap items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#98A1B2]" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Search name, card number or suggestion text…"
                       className="w-full border border-[#E6E9F0] rounded-md bg-white text-sm pl-9 pr-3 py-2" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2">
                <option value="">All statuses</option>
                <option value="Submitted">Submitted only</option>
                <option value="Pending">Pending only</option>
              </select>
              <select value={dept} onChange={(e) => setDept(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2">
                <option value="">All departments</option>
                {depts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="text-sm text-[#5A6377]">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="bg-white border border-[#E6E9F0] rounded-xl p-8 text-center text-sm text-[#5A6377]">
                No employees match these filters for {data?.month_label || 'this month'}.
              </div>
            ) : (
              <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F7F8FB] text-[11px] uppercase tracking-wider text-[#5A6377]">
                      <th className="text-left font-semibold px-4 py-3">Employee</th>
                      <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Department</th>
                      <th className="text-left font-semibold px-4 py-3">Status</th>
                      <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Submitted on</th>
                      <th className="text-left font-semibold px-4 py-3">Suggestion for {data?.month_label}</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.employee_id} data-testid={`suggestion-row-${r.employee_id}`}
                          className="border-t border-[#F2F4F8] align-top">
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#11182B]">{r.name}</div>
                          <div className="text-[11px] text-[#98A1B2]">{r.card_no || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-[#5A6377] hidden md:table-cell">
                          <div>{r.department || '—'}</div>
                          <div className="text-[11px] text-[#98A1B2]">{r.designation || ''}</div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill status={r.status} />
                          {r.is_late && (
                            <span data-testid="late-badge"
                              className="block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full
                                         bg-[#FBF0E6] text-[#B14A0E] whitespace-nowrap w-fit">
                              Late
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {r.submitted_at ? (
                            <>
                              <div className="text-[13px] text-[#11182B] tabular-nums">{formatDate(r.submitted_at)}</div>
                              <div className="text-[11px] text-[#98A1B2] tabular-nums">
                                {formatDateTime(r.submitted_at).split(', ').slice(1).join(', ')}
                              </div>
                            </>
                          ) : (
                            <span className="text-[#98A1B2] text-[13px]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {r.suggestions.length === 0 ? (
                            <span className="text-[#98A1B2] text-[13px]">Not submitted for this month</span>
                          ) : (
                            <div className="space-y-2">
                              {r.suggestions.map((s) => (
                                <div key={s.id} className="border border-[#F2F4F8] rounded-lg p-2.5 bg-[#FCFDFE]">
                                  <p className="text-[13px] text-[#11182B] whitespace-pre-wrap">{s.text}</p>
                                  <div className="flex items-center gap-2 mt-1.5 text-[11px] text-[#98A1B2] flex-wrap">
                                    <span>Submitted {formatDateTime(s.created_at)}</span>
                                    <span>· {s.source === 'self' ? 'Self (QR)' : 'HR'}</span>
                                    {s.status === 'reviewed' ? (
                                      <span className="text-[#15654B] inline-flex items-center gap-0.5">
                                        <Check className="w-3 h-3" /> Reviewed
                                      </span>
                                    ) : canReview ? (
                                      <button onClick={() => review(s)} className="text-[#2563EB] hover:underline">
                                        Mark reviewed
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" variant="outline" onClick={() => setHistoryFor(r.employee_id)}
                                  data-testid={`suggestion-history-${r.employee_id}`}>
                            History
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {historyFor && <HistoryDrawer employeeId={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}
